import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Building2, Save, AlertCircle, CheckCircle2, Upload, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import companySettingsService from '../../services/companySettingsService';

export default function CompanySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    companyLegalName: '',
    companyAbbreviation: '',
    companyLogoUrl: '',
    primaryColor: '#3b82f6',
    secondaryColor: '#10b981',
    contactEmail: '',
    contactPhone: '',
    companyAddress: '',
    taxIdentificationNumber: '',
    businessRegistrationNumber: '',
    industry: '',
    companySize: '',
    timezone: 'Pacific/Port_Moresby',
    currency: 'PGK',
    dateFormat: 'DD/MM/YYYY'
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companySettingsService?.getActiveCompanySettings();
      if (data) {
        setSettings(data);
        setFormData({
          companyName: data?.companyName || '',
          companyLegalName: data?.companyLegalName || '',
          companyAbbreviation: data?.companyAbbreviation || '',
          companyLogoUrl: data?.companyLogoUrl || '',
          primaryColor: data?.primaryColor || '#3b82f6',
          secondaryColor: data?.secondaryColor || '#10b981',
          contactEmail: data?.contactEmail || '',
          contactPhone: data?.contactPhone || '',
          companyAddress: data?.companyAddress || '',
          taxIdentificationNumber: data?.taxIdentificationNumber || '',
          businessRegistrationNumber: data?.businessRegistrationNumber || '',
          industry: data?.industry || '',
          companySize: data?.companySize || '',
          timezone: data?.timezone || 'Pacific/Port_Moresby',
          currency: data?.currency || 'PGK',
          dateFormat: data?.dateFormat || 'DD/MM/YYYY'
        });
      }
    } catch (err) {
      setError(err?.message || 'Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, or SVG)');
      return;
    }

    // Validate file size (max 2MB)
    if (file?.size > 2 * 1024 * 1024) {
      setError('Logo file size must be less than 2MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader?.result);
        setFormData(prev => ({
          ...prev,
          companyLogoUrl: reader?.result
        }));
      };
      reader?.readAsDataURL(file);
    } catch (err) {
      setError(err?.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({
      ...prev,
      companyLogoUrl: ''
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      if (settings?.id) {
        await companySettingsService?.updateCompanySettings(settings?.id, formData);
      } else {
        await companySettingsService?.createCompanySettings(formData);
      }

      setSuccess(true);
      await loadSettings();

      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Company Settings - White-Label Configuration</title>
        <meta name="description" content="Configure your company branding, details, and application customization for white-label deployment" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-foreground">Company Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure your company branding and details for white-label deployment
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Alert Messages */}
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Error</h3>
                <p className="text-sm text-destructive/90 mt-1">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-success">Success</h3>
                <p className="text-sm text-success/90 mt-1">Company settings saved successfully</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Information */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Company Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Company Name"
                  name="companyName"
                  value={formData?.companyName}
                  onChange={handleInputChange}
                  required
                  description="This will appear throughout the application"
                />
                <Input
                  label="Company Legal Name"
                  name="companyLegalName"
                  value={formData?.companyLegalName}
                  onChange={handleInputChange}
                  description="Full legal entity name"
                />
                <Input
                  label="Company Abbreviation"
                  name="companyAbbreviation"
                  value={formData?.companyAbbreviation}
                  onChange={handleInputChange}
                  description="Short form (e.g., ABC Ltd)"
                />
                <Input
                  label="Industry"
                  name="industry"
                  value={formData?.industry}
                  onChange={handleInputChange}
                  description="Your business sector"
                />
                <Input
                  label="Company Size"
                  name="companySize"
                  value={formData?.companySize}
                  onChange={handleInputChange}
                  description="Number of employees"
                />
              </div>
            </div>

            {/* Branding */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Branding</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Logo Upload Section */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Company Logo
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Upload your company logo (PNG, JPG, or SVG, max 2MB)
                  </p>
                  
                  {/* Logo Preview */}
                  {(logoPreview || formData?.companyLogoUrl) && (
                    <div className="relative inline-block">
                      <img
                        src={logoPreview || formData?.companyLogoUrl}
                        alt="Company Logo Preview"
                        className="h-24 w-auto object-contain border border-border rounded-lg p-2 bg-muted/30"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      <div className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors">
                        <Upload size={16} />
                        <span className="text-sm font-medium">
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </span>
                      </div>
                    </label>
                  </div>

                  {/* Alternative: Logo URL Input */}
                  <div className="pt-4 border-t border-border">
                    <Input
                      label="Or enter Logo URL"
                      name="companyLogoUrl"
                      type="url"
                      value={formData?.companyLogoUrl}
                      onChange={handleInputChange}
                      description="Direct URL to your company logo"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>

                {/* Color Pickers */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Primary Color"
                      name="primaryColor"
                      type="color"
                      value={formData?.primaryColor}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Secondary Color"
                      name="secondaryColor"
                      type="color"
                      value={formData?.secondaryColor}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="bg-muted/30 border border-border rounded-lg p-4">
                    <p className="text-xs font-medium text-foreground mb-2">Color Preview</p>
                    <div className="flex gap-2">
                      <div 
                        className="h-12 w-12 rounded-md border border-border" 
                        style={{ backgroundColor: formData?.primaryColor }}
                      />
                      <div 
                        className="h-12 w-12 rounded-md border border-border" 
                        style={{ backgroundColor: formData?.secondaryColor }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Contact Email"
                  name="contactEmail"
                  type="email"
                  value={formData?.contactEmail}
                  onChange={handleInputChange}
                  description="Primary contact email"
                />
                <Input
                  label="Contact Phone"
                  name="contactPhone"
                  type="tel"
                  value={formData?.contactPhone}
                  onChange={handleInputChange}
                  description="Primary contact number"
                />
                <div className="md:col-span-2">
                  <Input
                    label="Company Address"
                    name="companyAddress"
                    value={formData?.companyAddress}
                    onChange={handleInputChange}
                    description="Physical business address"
                  />
                </div>
              </div>
            </div>

            {/* Legal & Registration */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Legal & Registration</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Tax Identification Number (TIN)"
                  name="taxIdentificationNumber"
                  value={formData?.taxIdentificationNumber}
                  onChange={handleInputChange}
                  description="IRC tax identification number"
                />
                <Input
                  label="Business Registration Number"
                  name="businessRegistrationNumber"
                  value={formData?.businessRegistrationNumber}
                  onChange={handleInputChange}
                  description="IPA registration number"
                />
              </div>
            </div>

            {/* Regional Settings */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Regional Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Timezone"
                  name="timezone"
                  value={formData?.timezone}
                  onChange={handleInputChange}
                  description="Default timezone"
                />
                <Input
                  label="Currency"
                  name="currency"
                  value={formData?.currency}
                  onChange={handleInputChange}
                  description="Default currency code"
                />
                <Input
                  label="Date Format"
                  name="dateFormat"
                  value={formData?.dateFormat}
                  onChange={handleInputChange}
                  description="Date display format"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={loadSettings}
                disabled={saving}
              >
                Reset
              </Button>
              <Button
                type="submit"
                loading={saving}
                disabled={saving}
                iconName="Save"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
}